// ============================================================================
// AppEmbedPreview.tsx — превью встраиваемого контента (click-to-play).
// Берёт источник (url из сообщения/файл/объект) → resolveAppEmbed → карточка
// «▶ {name}» → по клику рендерит Embed поставщика (в AppEmbedHost, изоляция).
// Кнопка «Открыть в {name}» → переход (openUrl) / full-area.
// Приватность: НИКАКИХ авто-запросов к URL до клика (iframe не грузится).
// ============================================================================

import { useState } from 'react';
import { resolveAppEmbed, type EmbedSource } from '@/lib/appEmbeds';
import { AppEmbedHost } from '@/components/embed/AppEmbedHost';
import { createLogger } from '@/lib/logger';

const log = createLogger('AppEmbedPreview');

export function AppEmbedPreview({ source, onOpenApp }: { source: EmbedSource; onOpenApp?: (url: string) => void }) {
  const [active, setActive] = useState(false);
  const resolved = resolveAppEmbed(source);
  if (!resolved) return null;
  const { provider, meta } = resolved;

  const handleExpand = () => {
    if (provider.openUrl) {
      const url = provider.openUrl(meta);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    }
    onOpenApp?.(provider.appId);
  };

  const embedUrl = provider.embedUrl ? provider.embedUrl(meta) : '';
  const isExternal = !!embedUrl;

  return (
    <AppEmbedHost>
      <div className="rounded-md border border-border/70 overflow-hidden bg-muted/20">
        {!active ? (
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/40 transition-colors"
            onClick={() => setActive(true)}
          >
            <span className="text-lg leading-none">▶</span>
            <span className="text-xs truncate flex-1 text-left">{provider.name}</span>
            <button
              type="button"
              className="text-[10px] text-muted-foreground border rounded px-1.5 py-0.5 shrink-0 hover:bg-accent"
              onClick={(e) => { e.stopPropagation(); handleExpand(); }}
            >
              Открыть в {provider.name}
            </button>
          </button>
        ) : isExternal ? (
          <iframe
            src={embedUrl}
            className="w-full"
            style={{ aspectRatio: '16/9', border: 0 }}
            sandbox="allow-scripts allow-same-origin allow-presentation"
            allowFullScreen
            title={provider.name}
            loading="lazy"
          />
        ) : provider.Embed ? (
          <div className="p-2">
            <provider.Embed source={source} meta={meta} mode="active" onExpand={handleExpand} onError={(e) => log.warn('embed error: ' + e.message, e)} />
          </div>
        ) : (
          <div className="text-xs text-muted-foreground px-3 py-2">Встраиваемый контент недоступен</div>
        )}
      </div>
    </AppEmbedHost>
  );
}
